import { OverlayEventDetail } from '@ionic/core';
import { ModalController, PopoverController } from '@ionic/angular';
import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';

import { FoldersDataService, PropertyService } from '@services';
import { ReorderList } from '@shared/components/reorder-list/reorder-list.interfaces';
import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';
import { IProperty, PropertyDefaultValue, PropertySubject } from '@services/property/property-model.interfaces';
import { PropertyDefaultValueEditorPage } from '@modules/management/modules/property-picker/components';
import { FolderPickerService } from '@modules/management/modules/folders/services/folder-picker.service';
import { ITreeViewModalConfig, TreeViewNode, TreeViewNodeIdType } from '@shared/modules/tree-viewer/models';
import {
  ViewModalTreePopupComponent
} from '@shared/modules/tree-viewer/view-modals/view-modal-popup/view-modal-popup.component';
import {
  FolderDataType,
  FolderPickerConfig,
  FolderPickerEntities,
  IFolder
} from '@modules/management/modules/folders/model/folders.interfaces';
import {
  ListColumn,
  PropertyPickerConfig,
  PropertyPickerSelection,
  ReorderPickerFolder,
  ReorderPickerItem,
  ReorderPickerProperty,
  SelectedProperty
} from './../../model/property-picker.interfaces';

import { clone, cloneDeep, filter, find, has, includes, map, split } from 'lodash';

@Component({
  selector: 'app-property-picker',
  templateUrl: './property-picker.component.html',
  styleUrls: ['./property-picker.component.scss'],
})
export class PropertyPickerComponent extends CustomFormComponent implements OnInit {
  @Input() parentIds: PropertyPickerSelection[];
  @Input() pickerConfig: PropertyPickerConfig;
  @ViewChild('propertyNameRef', { static: true }) propertyNameRef: TemplateRef<HTMLElement>;
  @ViewChild('fullPropertyNameRef', { static: true }) fullPropertyNameRef: TemplateRef<HTMLElement>;
  @ViewChild('propertyStateRef', { static: true }) propertyStateRef: TemplateRef<HTMLElement>;
  @ViewChild('requiredRef', { static: true }) requiredRef: TemplateRef<HTMLElement>;
  @ViewChild('propertyHiddenRef', { static: true }) propertyHiddenRef: TemplateRef<HTMLElement>;
  @ViewChild('propertyDefaultValueRef', { static: true }) propertyDefaultValueRef: TemplateRef<HTMLElement>;

  public title = {
    main: '',
    parent: '',
    child: ''
  };
  public reorderConfig: ReorderList<ReorderPickerItem> = {
    draggable: true,
    items: [],
    columns: []
  };
  public parentReorderConfig: ReorderList<ReorderPickerItem> = clone(this.reorderConfig);

  private selectedProperties: PropertyPickerSelection[];
  private propertyEntities: FolderPickerEntities = {
    folderItems: [],
    folders: {}
  };

  constructor(
    private popoverController: PopoverController,
    private folderPickerService: FolderPickerService,
    private propertyService: PropertyService,
    private foldersDataService: FoldersDataService,
    private modalController: ModalController
  ) {
    super();
  }

  ngOnInit() {
    this.defineConfig();
    this.defineTitle();
    this.getPropertyNodes();
    this.selectedProperties = cloneDeep(this.formValue);
    this.initReorderList();
  }

  public emitValue() {
    this.formValue = this.selectedProperties?.length ? this.selectedProperties : null;
  }

  public onReorder(properties: ReorderPickerItem[]) {
    this.selectedProperties = map(properties, (property) => {
      if ((property as ReorderPickerProperty).propertyID) {
        return find(this.selectedProperties, { propertyID: (property as ReorderPickerProperty).propertyID });
      } else {
        return find(this.selectedProperties, { propertyFolderID: property.folderID });
      }
    });

    this.emitValue();
  }

  public selectProperties() {
    this.popoverController.create({
      component: ViewModalTreePopupComponent,
      animated: false,
      componentProps: {
        config: <ITreeViewModalConfig>{
          title: 'PROPERTY.Select_Property',
          nodes: this.getPropertyNodes(),
          multiselect: this.pickerConfig?.viewTree?.multiselect ?? true,
          disableParentSelection: this.pickerConfig?.viewTree?.disableParentSelection ?? false
        }
      }
    }).then((element: HTMLIonPopoverElement) => {
      element.present();

      element.onDidDismiss().then((event: OverlayEventDetail<TreeViewNodeIdType[]>) => {
        if (event.data) {
          this.onSelectedProperties(event.data);
        }
      });
    });
  }

  public onStateChanged(value: boolean, property: IProperty | IFolder) {
    const selectedProperty = this.findFromSelectedProperty(property);

    if (selectedProperty) {
      selectedProperty.healthRelated = value ? 1 : 0;
    }

    this.emitValue();
  }

  public toggleHiddenValue(value: boolean, property: ReorderPickerItem) {
    const selectedProperty = this.findFromSelectedProperty(property);

    if (selectedProperty) {
      selectedProperty.hidden = !!value;
    }

    this.emitValue();
  }

  public toggleRequiredValue(value: boolean, property: ReorderPickerItem) {
    const selectedProperty = this.findFromSelectedProperty(property);

    if (selectedProperty) {
      selectedProperty.required = value ? 1 : 0;
    }

    this.emitValue();
  }

  public changeValue(property: IProperty) {
    this.modalController.create({
      component: PropertyDefaultValueEditorPage,
      cssClass: 'fullscreen-modal',
      backdropDismiss: false,
      componentProps: {
        property: property
      }
    }).then((modal) => {
      modal.present();

      modal.onDidDismiss().then((event) => {
        if (event.data) {
          const selectedProperty = this.findFromSelectedProperty(property);
          property.defaultValue = event.data;

          if (selectedProperty) {
            selectedProperty.defaultValue = property.defaultValue;
          }

          this.emitValue();
          this.initReorderList();
        }
      });
    });
  }

  private findFromSelectedProperty(property: IProperty | IFolder) {
    let selectedProperty: SelectedProperty;

    if ((property as IProperty).propertyID) {
      selectedProperty = find(this.selectedProperties,  { propertyID: (property as IProperty).propertyID });
    } else if (has(property, 'folderID')) {
      selectedProperty = find(this.selectedProperties,  { propertyFolderID: property.folderID });
    }

    return selectedProperty;
  }

  private onSelectedProperties(ids: TreeViewNodeIdType[]) {
    this.selectedProperties = map(ids, (nodeId: TreeViewNodeIdType): PropertyPickerSelection => {
      const folderID = +split(nodeId as string, 'folder:')?.[1];

      if (isNaN(folderID)) {
        const selectedProperty = find(this.reorderConfig.items, { propertyID: nodeId }) as ReorderPickerProperty;
        const property = this.propertyService.getPropertyById(nodeId as number);

        return {
          propertyID: nodeId as number,
          defaultValue: selectedProperty?.defaultValue ?? property.defaultValue,
          healthRelated: selectedProperty?.healthRelated ?? property.healthRelated,
          hidden: selectedProperty?.hidden ?? this.propertyService.isPropertyHidden(property)
        };
      } else {
        return { propertyFolderID: folderID };
      }
    });

    this.emitValue();
    this.initReorderList();
  }

  private defineTitle() {
    if (this.pickerConfig.type === PropertySubject.Asset) {
      this.title.main = 'MGMT_LIST.Asset_Properties';

      if (this.parentIds?.length) {
        this.title.parent = 'MGMT_LIST.Asset_Properties_Inherited';
        this.title.child = 'MGMT_LIST.Asset_Specific_Properties';
      }
    } else {
      this.title.main = 'PROPERTY.Properties';
    }

    if (this.pickerConfig?.selectedList?.title) {
      this.title.main = this.pickerConfig.selectedList.title;
    }

    if (this.pickerConfig?.selectedList?.inheritedListTitle) {
      this.title.child = this.pickerConfig.selectedList.inheritedListTitle;
    }

    if (this.pickerConfig?.selectedList?.title) {
      this.title.main = this.pickerConfig.selectedList.title;
    }
  }

  private getPropertyNodes(): TreeViewNode[] {
    const properties = cloneDeep(filter(this.propertyService.getProperties(), (property) => {
      const customFilter = (property: IProperty) => {
        return this.pickerConfig?.viewTree?.filter(property) ?? true;
      }
      return !property.disabledAt && includes(property.subjects, this.pickerConfig.type) && property.active === 1 && customFilter(property);
    }));

    const config: FolderPickerConfig = {
      dataType: FolderDataType.PROPERTY,
      entities: this.propertyEntities,
      selectAllLabel: 'PROPERTY.All_Properties',
      selectedIds: this.formValue,
      parentIds: [], // Allow re-selection of inherited properties for local override
      getFolderItems: () => properties,
      getItemId: (property: IProperty) => property?.propertyID,
      getItemTitle: (property: IProperty) => property?.title,
      getFolderId: (folder) => folder?.folderID || (folder as SelectedProperty)?.propertyFolderID
    };
    return this.folderPickerService.getTreeNodes(config);
  }

  private initReorderList() {
    this.reorderConfig.columns = this.getColumns();
    this.parentReorderConfig.columns = clone(this.reorderConfig.columns);

    this.reorderConfig.items = this.defineReorderItems(this.selectedProperties);
    this.parentReorderConfig.items = this.defineReorderItems(this.parentIds);
  }

  private defineReorderItems(selectedProperties: SelectedProperty[]): ReorderPickerItem[] {
    return filter(map(selectedProperties, (selectedProperty) => {
      if (selectedProperty.propertyID) {
        const property = this.propertyService.getPropertyById(selectedProperty.propertyID);
        const propertyPickerItem = find(this.reorderConfig.items, { propertyID: selectedProperty.propertyID });
        const pickerValue: PropertyPickerSelection = find(this.formValue, { propertyID: selectedProperty.propertyID });
        property.defaultValue = this.getDefultValue(selectedProperty, property.defaultValue);

        return {
          ...property,
          healthRelated: propertyPickerItem?.healthRelated ?? selectedProperty.healthRelated,
          hidden: (propertyPickerItem as SelectedProperty)?.hidden ?? selectedProperty.hidden ?? this.propertyService.isPropertyHidden(property),
          required: selectedProperty.required
        };
      } else if (has(selectedProperty, 'propertyFolderID')) {
        const folder = this.foldersDataService.getFolderByID(selectedProperty.propertyFolderID);
        const currentFolder = find(this.reorderConfig.items, { folderID: selectedProperty.propertyFolderID });
        const healthRelated = currentFolder?.healthRelated ?? selectedProperty.healthRelated;
        const hidden = (currentFolder as SelectedProperty)?.hidden ?? selectedProperty.hidden ?? this.foldersDataService.isHidden(folder);

        if (selectedProperty.propertyFolderID === 0) {
          return {
            title: 'PROPERTY.All_Properties',
            folderID: 0,
            healthRelated,
            hidden
          } as ReorderPickerFolder;
        }

        return {
          ...folder,
          healthRelated,
          hidden
        };
      }
    }));
  }

  private defineConfig() {
    this.reorderConfig.draggable = this.pickerConfig?.selectedList?.draggable ?? true;
  }

  private getColumns() {
    const columnKeys = this.pickerConfig?.selectedList?.columns ?? [
      ListColumn.PropertyName,
      ListColumn.TrackState,
      ListColumn.Hidden,
      ListColumn.DefaultValue
    ];
    const columnMap = {
      [ListColumn.PropertyName]:  {
        title: 'SHARED.Property',
        class: 'property-name',
        template: this.propertyNameRef
      },
      [ListColumn.FullPropertyName]:  {
        title: 'SHARED.Property',
        class: 'property-name',
        template: this.fullPropertyNameRef
      },
      [ListColumn.Required]: {
        title: 'SHARED.Required',
        class: 'property-state',
        template: this.requiredRef
      },
      [ListColumn.TrackState]: {
        title: 'PROPERTY.Track_State',
        class: 'property-state',
        template: this.propertyStateRef
      },
      [ListColumn.Hidden]: {
        title: 'SHARED.Hidden',
        class: 'property-state',
        template: this.propertyHiddenRef
      },
      [ListColumn.DefaultValue]: {
        title: 'PROPERTY.Default_Value',
        class: 'property-default-value',
        template: this.propertyDefaultValueRef
      }
    };

    return filter(map(columnKeys, (key) => columnMap[key]));
  }

   private getDefultValue(selectedProperty: SelectedProperty, defaultValue: PropertyDefaultValue): PropertyDefaultValue {
    const pickerValue: PropertyPickerSelection = find(this.formValue, { propertyID: selectedProperty.propertyID });

    if (!pickerValue && selectedProperty.defaultValue) {
      defaultValue = selectedProperty.defaultValue;
    }

    if (pickerValue?.defaultValue || !defaultValue) {
      defaultValue = pickerValue?.defaultValue;
    }

    return defaultValue;
  }
}
